import React, { useState, useEffect } from 'react';
import OptimizedImage from '@/components/OptimizedImage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Heart, Share2, MessageCircle, MapPin, Package, Truck, Clock, Award, FileText, 
  ChevronLeft, ChevronRight, Phone, Mail, Camera, Star, DollarSign, Wrench, Box,
  Download, CheckCircle, AlertCircle, AlertTriangle, Lock, Shield
} from 'lucide-react';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { useAuth } from '@/components/AuthProvider';
import { cn } from '@/lib/utils';
import { UniversalMap } from '@/components/maps';
import { useIsDesktop } from '@/hooks/use-tablet';
import { useFavorites } from '@/hooks/useFavorites';
import { ShareListingButton } from '@/components/ShareListingButton';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';
import { supabase } from '@/integrations/supabase/client';
import { useEffectiveVisibility } from '@/hooks/useEffectiveVisibility';
import { SellerSection } from '@/components/listing/SellerSection';
import { useViewTracking } from '@/hooks/useViewTracking';

interface PartsListingDetailModalProps {
  listing: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFavorite?: () => void;
  onShare?: () => void;
  onContact?: () => void;
  isFavorited?: boolean;
}

const PartsListingDetailModal = ({
  listing,
  open,
  onOpenChange,
  onFavorite,
  onShare,
  onContact,
  isFavorited = false,
}: PartsListingDetailModalProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [documents, setDocuments] = useState<any[]>([]);
  const [sellerProfile, setSellerProfile] = useState<any>(null);
  
  // Track views when modal opens
  useViewTracking(listing?.id || null, open);
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const { openUnifiedContact } = useUnifiedContact();

  const isOwner = user?.id === listing?.user_id;
  
  // Use grant-aware visibility
  const {
    blurPrice: shouldHidePrice,
    blurLocation: shouldHideLocation,
    blurSerial: shouldHideSerial,
    blurHours: shouldHideHours,
    blurContact: shouldHideContact,
  } = useEffectiveVisibility(listing?.id, listing?.visibility_settings, listing?.user_id);

  // Handler for Contact Seller using unified modal
  const handleContact = () => {
    if (!listing) return;
    // Auth gate: redirect to sign-in if not logged in
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to contact this seller' }
      }));
      return;
    }
    const confidentialDocs = documents
      .filter(doc => doc.is_confidential)
      .map(doc => ({
        id: doc.id,
        document_name: doc.document_name,
        document_category: doc.document_category,
        has_access: doc.has_access,
      }));
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title || listing.name,
      sellerId: listing.user_id || '',
      assetType: 'parts',
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        serial: shouldHideSerial,
        hours: shouldHideHours,
        contact: shouldHideContact,
      },
      confidentialDocuments: confidentialDocs.length > 0 ? confidentialDocs : undefined,
    });
    onOpenChange(false);
  };

  // Helper to request a specific field (auth-gated)
  const requestField = (field: string) => {
    if (!listing) return;
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to request this information' }
      }));
      return;
    }
    const confidentialDocs = documents
      .filter(doc => doc.is_confidential)
      .map(doc => ({
        id: doc.id,
        document_name: doc.document_name,
        document_category: doc.document_category,
        has_access: doc.has_access,
      }));
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title || listing.name,
      sellerId: listing.user_id || '',
      assetType: 'parts',
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        serial: shouldHideSerial,
        hours: shouldHideHours,
        contact: shouldHideContact,
      },
      preselectedFields: [field],
      confidentialDocuments: confidentialDocs.length > 0 ? confidentialDocs : undefined,
    });
    onOpenChange(false);
  };

  // Fetch documents and seller profile using edge function when modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (!listing?.id || !open) return;
      
      // Fetch seller profile
      if (listing.user_id) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, user_id, display_name, avatar_url, company_name, company_logo_url, represent_as_company, user_type, is_verified, created_at, dealer_status')
          .eq('user_id', listing.user_id)
          .maybeSingle();
        setSellerProfile(profileData);
      }
      // Fetch documents
      try {
        const { data: session } = await supabase.auth.getSession();
        const response = await fetch('https://dvawogvuaomipakwlvst.supabase.co/functions/v1/get-listing-documents', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session?.session?.access_token && {
              'Authorization': `Bearer ${session.session.access_token}`
            })
          },
          body: JSON.stringify({ listingId: listing.id, assetType: 'parts' })
        });
        
        if (response.ok) {
          const result = await response.json();
          setDocuments(result.documents || []);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        // Fallback to direct fetch
        const { data } = await supabase
          .from('listing_documents')
          .select('*')
          .eq('listing_id', listing.id);
        if (data) setDocuments(data);
      }
    };
    fetchData();
    setCurrentImageIndex(0);
  }, [listing?.id, open, listing?.user_id]);

  // Early return if listing is null to prevent accessing null properties
  if (!listing) return null;

  const allPhotos = [
    ...(listing.hero_image ? [listing.hero_image] : []),
    ...(listing.photos || []),
  ];

  const hasImages = allPhotos.length > 0;
  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % allPhotos.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);

  const handleFavoriteToggle = async () => {
    if (onFavorite) {
      onFavorite();
    } else {
      await toggleFavorite(listing.id, 'parts');
    }
  };

  const formatNumber = (num?: number) => num ? new Intl.NumberFormat('en-US').format(num) : 'N/A';

  const isBuyRequest = listing.listing_type?.includes('buy') || listing.listing_type === 'parts_rent_request';

  // Badge helpers
  const getListingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      parts_sell_aircraft: 'For Sale',
      parts_sell_engine: 'For Sale',
      parts_sell_general: 'For Sale',
      parts_rent: 'For Rent',
      parts_buy_aircraft: 'Wanted to Buy',
      parts_buy_engine: 'Wanted to Buy',
      parts_buy_general: 'Wanted to Buy',
      parts_rent_request: 'Wanted to Rent'
    };
    return labels[type] || 'Listing';
  };

  const getListingTypeBadgeStyle = (type: string) => {
    if (type?.includes('sell')) return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
    if (type === 'parts_rent') return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
    return 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20';
  };

  const getItemTypeIcon = (type: string) => {
    if (type === 'tool') return <Wrench className="w-3 h-3" />;
    if (type === 'equipment') return <Box className="w-3 h-3" />;
    return <Package className="w-3 h-3" />;
  };

  const getConditionColor = (condition?: string) => {
    const colors: Record<string, string> = {
      new: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20',
      overhauled: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
      serviceable: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border-cyan-500/20',
      as_removed: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
      needs_repair: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    };
    return colors[condition || ''] || 'bg-muted text-muted-foreground';
  };

  const formatShippingMethod = (method: string) => {
    const labels: Record<string, string> = {
      ground: 'Ground Shipping',
      air_freight: 'Air Freight',
      courier: 'Courier (FedEx, UPS, DHL)',
    };
    return labels[method] || method.replace(/_/g, ' ');
  };

  // Touch swipe state
  const touchStartX = React.useRef<number | null>(null);
  const touchEndX = React.useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextImage();
      else prevImage();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Professional Photo Gallery
  const renderPhotoGallery = () => (
    <Card className="overflow-hidden mb-4 sm:mb-6">
      <div 
        className={cn(
          "relative bg-muted",
          isMobile ? "aspect-[4/3]" : "aspect-video"
        )}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        {hasImages ? (
          <>
            <OptimizedImage
              src={allPhotos[currentImageIndex]}
              alt={`${listing.title} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-contain touch-manipulation"
              size="full"
            />
            {/* Counter - centered bottom on mobile */}
            <div className={cn(
              "absolute bg-black/70 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 backdrop-blur-sm",
              isMobile ? "bottom-3 left-1/2 -translate-x-1/2" : "top-3 right-3"
            )}>
              <Camera className="w-3.5 h-3.5" />
              {currentImageIndex + 1} / {allPhotos.length}
            </div>
            
            {/* Dot indicators for mobile */}
            {isMobile && allPhotos.length > 1 && allPhotos.length <= 10 && (
              <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-1.5">
                {allPhotos.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    aria-label={`View photo ${idx + 1}`}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      idx === currentImageIndex ? "bg-white scale-110" : "bg-white/50"
                    )}
                  />
                ))}
              </div>
            )}
            
            {/* Nav buttons - desktop only */}
            {!isMobile && allPhotos.length > 1 && (
              <>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white min-h-[44px] min-w-[44px] rounded-full"
                  onClick={prevImage}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white min-h-[44px] min-w-[44px] rounded-full"
                  onClick={nextImage}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No photos available</p>
            </div>
          </div>
        )}
      </div>
      {hasImages && allPhotos.length > 1 && (
        <div className="p-2 sm:p-3 border-t bg-muted/30 overflow-x-auto scrollbar-hide">
          <div className="flex gap-1.5 sm:gap-2">
            {allPhotos.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                aria-label={`Select photo ${idx + 1}`}
                className={cn(
                  "flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isMobile ? "w-12 h-12" : "w-16 h-16",
                  idx === currentImageIndex ? "border-primary ring-2 ring-primary/20" : "border-transparent hover:border-muted-foreground/30"
                )}
              >
                <OptimizedImage src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" size="thumbnail" />
              </button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );

  // Price display helper
  const renderPrice = () => {
    const price = listing.price || listing.rental_rate;
    const budget = listing.max_budget || listing.max_rate;
    
    if (shouldHidePrice) {
      return (
        <BlurredPrivateData
          placeholderContent={
            <div>
              <p className="text-xs text-muted-foreground mb-1">{isBuyRequest ? 'Budget' : 'Price'}</p>
              <p className="text-2xl md:text-3xl font-bold text-muted-foreground">$XX,XXX</p>
            </div>
          }
          fieldName="Price"
          size="lg"
          onRequestInfo={isOwner ? undefined : () => requestField('price')}
        />
      );
    }
    
    if (isBuyRequest && budget) {
      return (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Budget</p>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
            Up to ${formatNumber(budget)}
            {listing.rental_period && listing.max_rate && (
              <span className="text-base font-normal text-muted-foreground"> / {listing.rental_period === 'flight_hour' ? 'FLT hr' : listing.rental_period}</span>
            )}
          </p>
        </div>
      );
    }
    
    if (listing.rental_rate) {
      return (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Rental Rate</p>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            ${formatNumber(listing.rental_rate)}
            <span className="text-base font-normal text-muted-foreground">/{listing.rental_period === 'flight_hour' ? 'FLT hr' : (listing.rental_period || 'day')}</span>
          </p>
        </div>
      );
    }
    
    if (price) {
      return (
        <div>
          <p className="text-xs text-muted-foreground mb-1">Asking Price</p>
          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            ${formatNumber(price)}
          </p>
          {listing.price_negotiable && (
            <Badge variant="secondary" className="mt-1">Negotiable</Badge>
          )}
        </div>
      );
    }
    
    return <p className="text-lg text-muted-foreground">Price on request</p>;
  };

  // Specifications rendering
  const renderSpecifications = () => {
    const itemType = listing.item_type || 'part';

    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Part-specific specs */}
        {itemType === 'part' && (
          <>
            {listing.tsn != null && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-lg border border-blue-500/20">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="text-xs text-muted-foreground">TSN</p>
                  {shouldHideHours ? (
                    <BlurredPrivateData placeholderContent={<span className="font-semibold text-blue-700 dark:text-blue-400">X,XXX hrs</span>} fieldName="Hours" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('hours')} />
                  ) : (
                    <p className="font-semibold text-blue-700 dark:text-blue-400">{formatNumber(listing.tsn)} hrs</p>
                  )}
                </div>
              </div>
            )}
            {listing.csn != null && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-lg border border-purple-500/20">
                <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <div>
                  <p className="text-xs text-muted-foreground">CSN</p>
                  {shouldHideHours ? (
                    <BlurredPrivateData placeholderContent={<span className="font-semibold text-purple-700 dark:text-purple-400">X,XXX</span>} fieldName="Cycles" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('hours')} />
                  ) : (
                    <p className="font-semibold text-purple-700 dark:text-purple-400">{formatNumber(listing.csn)}</p>
                  )}
                </div>
              </div>
            )}
            {listing.tso != null && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-lg border border-emerald-500/20">
                <Wrench className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <div>
                  <p className="text-xs text-muted-foreground">TSO</p>
                  {shouldHideHours ? (
                    <BlurredPrivateData placeholderContent={<span className="font-semibold text-emerald-700 dark:text-emerald-400">X,XXX hrs</span>} fieldName="Hours" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('hours')} />
                  ) : (
                    <p className="font-semibold text-emerald-700 dark:text-emerald-400">{formatNumber(listing.tso)} hrs</p>
                  )}
                </div>
              </div>
            )}
            {listing.cso != null && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-lg border border-amber-500/20">
                <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                <div>
                  <p className="text-xs text-muted-foreground">CSO</p>
                  {shouldHideHours ? (
                    <BlurredPrivateData placeholderContent={<span className="font-semibold text-amber-700 dark:text-amber-400">X,XXX</span>} fieldName="Cycles" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('hours')} />
                  ) : (
                    <p className="font-semibold text-amber-700 dark:text-amber-400">{formatNumber(listing.cso)}</p>
                  )}
                </div>
              </div>
            )}
            {listing.tbo && (
              <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-sky-500/10 to-sky-500/5 rounded-lg border border-sky-500/20">
                <Award className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                <div>
                  <p className="text-xs text-muted-foreground">TBO</p>
                  <p className="font-semibold text-sky-700 dark:text-sky-400">{formatNumber(listing.tbo)} hrs</p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tool-specific specs */}
        {itemType === 'tool' && (
          <>
            {listing.tool_type && (
              <div className="p-3 bg-muted/50 rounded-lg sm:col-span-2">
                <p className="text-xs text-muted-foreground">Tool Type</p>
                <p className="font-semibold break-words">{listing.tool_type}</p>
              </div>
            )}
            {listing.calibration_status && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Calibration Status</p>
                <Badge variant="outline" className={cn('capitalize', listing.calibration_status === 'current' ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'bg-amber-500/10 text-amber-700 dark:text-amber-400')}>
                  {listing.calibration_status.replace(/_/g, ' ')}
                </Badge>
              </div>
            )}
            {listing.calibration_due_date && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Calibration Due</p>
                <p className="font-semibold">{new Date(listing.calibration_due_date).toLocaleDateString()}</p>
              </div>
            )}
          </>
        )}

        {/* Equipment-specific specs */}
        {itemType === 'equipment' && (
          <>
            {listing.equipment_category && (
              <div className="p-3 bg-muted/50 rounded-lg sm:col-span-2">
                <p className="text-xs text-muted-foreground">Category</p>
                <p className="font-semibold break-words">{listing.equipment_category}</p>
              </div>
            )}
            {listing.operating_weight && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Operating Weight</p>
                <p className="font-semibold">{listing.operating_weight} lb</p>
              </div>
            )}
          </>
        )}

        {/* Common specs */}
        {listing.part_number && (
          <div className="p-3 bg-muted/50 rounded-lg sm:col-span-2">
            <p className="text-xs text-muted-foreground">Part Number</p>
            <p className="font-mono font-semibold break-all">{listing.part_number}</p>
          </div>
        )}
        {listing.serial_number && !isBuyRequest && (
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground">Serial Number</p>
            {shouldHideSerial ? (
              <BlurredPrivateData placeholderContent={<span className="font-mono font-semibold">XXXXXX</span>} fieldName="Serial Number" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('serial')} />
            ) : (
              <p className="font-mono font-semibold">{listing.serial_number}</p>
            )}
          </div>
        )}
        {listing.material && (
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground">Material</p>
            <p className="font-semibold">{listing.material}</p>
          </div>
        )}
        {listing.weight && (
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground">Weight</p>
            <p className="font-semibold">{listing.weight} {listing.weight_unit || 'lb'}</p>
          </div>
        )}
        {listing.power_requirements && (
          <div className="p-3 bg-muted/50 rounded-lg sm:col-span-2">
            <p className="text-xs text-muted-foreground">Power Requirements</p>
            <p className="font-semibold break-words">{listing.power_requirements}</p>
          </div>
        )}
      </div>
    );
  };

  // Tabs content
  const renderTabs = (compact: boolean = false) => (
    <Tabs defaultValue="overview" className="space-y-3 sm:space-y-4">
      <TabsList className={cn(
        "h-auto p-1 gap-1",
        compact 
          ? "flex overflow-x-auto scrollbar-hide justify-start w-full after:content-[''] after:w-4 after:flex-shrink-0" 
          : "grid w-full grid-cols-4"
      )}>
        <TabsTrigger value="overview" className={cn(
          "min-h-[40px] sm:min-h-[44px] data-[state=active]:bg-background",
          compact ? "flex-shrink-0 px-3 text-xs gap-1.5" : "text-xs md:text-sm"
        )}>
          {compact && <FileText className="w-3.5 h-3.5" />}
          Overview
        </TabsTrigger>
        <TabsTrigger value="specs" className={cn(
          "min-h-[40px] sm:min-h-[44px] data-[state=active]:bg-background",
          compact ? "flex-shrink-0 px-3 text-xs gap-1.5" : "text-xs md:text-sm"
        )}>
          {compact && <Package className="w-3.5 h-3.5" />}
          Specs
        </TabsTrigger>
        <TabsTrigger value="documents" className={cn(
          "min-h-[40px] sm:min-h-[44px] data-[state=active]:bg-background",
          compact ? "flex-shrink-0 px-3 text-xs gap-1.5" : "text-xs md:text-sm"
        )}>
          {compact && <Award className="w-3.5 h-3.5" />}
          Docs
        </TabsTrigger>
        <TabsTrigger value="location" className={cn(
          "min-h-[40px] sm:min-h-[44px] data-[state=active]:bg-background",
          compact ? "flex-shrink-0 px-3 text-xs gap-1.5" : "text-xs md:text-sm"
        )}>
          {compact && <MapPin className="w-3.5 h-3.5" />}
          Location
        </TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-4 mt-4">

        {/* Description */}
        {listing.description && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{listing.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Item Details */}
        {(listing.part_number || (!isBuyRequest && listing.serial_number) || listing.quantity) && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">Item Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {listing.part_number && (
                <div>
                  <p className="text-xs text-muted-foreground">Part Number</p>
                  <p className="font-mono font-medium">{listing.part_number}</p>
                </div>
              )}
              {listing.serial_number && !isBuyRequest && (
                <div>
                  <p className="text-xs text-muted-foreground">Serial Number</p>
                  {shouldHideSerial ? (
                    <BlurredPrivateData placeholderContent={<span className="font-mono font-medium">XXXXXX</span>} fieldName="Serial Number" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('serial')} />
                  ) : (
                    <p className="font-mono font-medium">{listing.serial_number}</p>
                  )}
                </div>
              )}
              {listing.quantity && (
                <div>
                  <p className="text-xs text-muted-foreground">Quantity</p>
                  <p className="font-medium">{listing.quantity}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Shipping Information */}
        {(listing.local_pickup_available || listing.domestic_shipping || listing.international_shipping) && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Shipping Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {listing.local_pickup_available && <Badge variant="secondary">Local Pickup</Badge>}
                {listing.domestic_shipping && <Badge variant="secondary">Domestic Shipping</Badge>}
                {listing.international_shipping && <Badge variant="secondary">International Shipping</Badge>}
              </div>
              {listing.shipping_methods?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {listing.shipping_methods.map((method: string, idx: number) => (
                    <Badge key={idx} variant="outline">{formatShippingMethod(method)}</Badge>
                  ))}
                </div>
              )}
              {listing.handling_time && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Handling Time:</span>
                  <span className="font-medium">{listing.handling_time}</span>
                </div>
              )}
              {listing.hazmat_required && (
                <Badge variant="outline" className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20">
                  <AlertTriangle className="w-3 h-3 mr-1" /> Hazmat Required
                </Badge>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rental Terms */}
        {listing.listing_type === 'parts_rent' && (listing.minimum_term || listing.deposit || listing.maintenance_terms) && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Rental Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {listing.minimum_term && (
                <div>
                  <p className="text-xs text-muted-foreground">Minimum Term</p>
                  <p className="font-medium">{listing.minimum_term} days</p>
                </div>
              )}
              {listing.deposit && (
                <div>
                  <p className="text-xs text-muted-foreground">Security Deposit</p>
                  <p className="font-medium">${formatNumber(listing.deposit)}</p>
                </div>
              )}
              {listing.maintenance_terms && (
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Maintenance Terms</p>
                  <p className="text-sm">{listing.maintenance_terms}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contact Information */}
        {(listing.contact_name || listing.contact_email || listing.contact_phone) && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                {isBuyRequest ? 'Buyer Contact' : 'Seller Contact'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {shouldHideContact ? (
                <BlurredPrivateData
                  placeholderContent={
                    <div className="space-y-2">
                      <p className="font-medium">Contact Name</p>
                      <div className="flex gap-2">
                        <span>Email</span>
                        <span>Phone</span>
                      </div>
                    </div>
                  }
                  fieldName="Contact Information"
                  size="md"
                  onRequestInfo={isOwner ? undefined : () => requestField('contact')}
                />
              ) : (
                <>
                  {listing.contact_name && <p className="font-medium">{listing.contact_name}</p>}
                  <div className="flex flex-wrap gap-2">
                    {listing.contact_email && (
                      <Button variant="outline" size="sm" asChild className="min-h-[44px]">
                        <a href={`mailto:${listing.contact_email}`}>
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </a>
                      </Button>
                    )}
                    {listing.contact_phone && (
                      <Button variant="outline" size="sm" asChild className="min-h-[44px]">
                        <a href={`tel:${listing.contact_phone}`}>
                          <Phone className="w-4 h-4 mr-2" />
                          Call
                        </a>
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Certifications */}
        {listing.certifications?.length > 0 && (
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-4 h-4" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {listing.certifications.map((cert: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {cert}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="specs" className="mt-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">Technical Specifications</CardTitle>
          </CardHeader>
          <CardContent>
            {renderSpecifications()}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="documents" className="space-y-4 mt-4">
        {documents.length > 0 ? (
          <div className="space-y-2">
            {documents.map((doc) => {
              // Document is locked if: confidential AND not owner AND no access grant
              const isLocked = doc.is_confidential && !isOwner && !doc.has_access;
              return (
                <Card key={doc.id} className="p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                        isLocked ? "bg-blue-500/10" : "bg-primary/10"
                      )}>
                        {isLocked ? <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400" /> : <FileText className="w-5 h-5 text-primary" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{doc.document_name}</p>
                        <div className="flex items-center gap-2">
                          {doc.document_category && (
                            <p className="text-xs text-muted-foreground">{doc.document_category}</p>
                          )}
                          {doc.is_confidential && (
                            <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20">Confidential</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {isLocked ? (
                      <Button variant="outline" size="sm" onClick={handleContact} className="min-h-[40px] flex-shrink-0">
                        <Lock className="w-4 h-4 mr-2" />
                        Request Access
                      </Button>
                    ) : doc.file_url ? (
                      <Button variant="outline" size="sm" asChild className="min-h-[40px] flex-shrink-0">
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </a>
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" disabled className="min-h-[40px] flex-shrink-0">
                        <Download className="w-4 h-4 mr-2" />
                        Unavailable
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : listing.documents?.length > 0 ? (
          <div className="space-y-2">
            {listing.documents.map((doc: any, idx: number) => (
              <Card key={idx} className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">{doc.name || `Document ${idx + 1}`}</span>
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <a href={doc.url} target="_blank" rel="noopener noreferrer">View</a>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No documents available</p>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="location" className="mt-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            {shouldHideLocation ? (
              <BlurredPrivateData
                placeholderContent={
                  <div className="h-48 bg-muted rounded-lg flex items-center justify-center">
                    <MapPin className="w-12 h-12 text-muted-foreground/50" />
                  </div>
                }
                fieldName="Location"
                size="lg"
                onRequestInfo={isOwner ? undefined : () => requestField('location')}
              />
            ) : listing.latitude && listing.longitude ? (
              <UniversalMap
                singleMarker={{ position: { lat: listing.latitude, lng: listing.longitude }, listingType: listing.listing_type }}
                assetType="parts"
              />
            ) : (listing.city || listing.country) ? (
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-center gap-3">
                <MapPin className="w-5 h-5 text-primary" />
                <span className="font-medium">{[listing.city, listing.country].filter(Boolean).join(', ')}</span>
              </div>
            ) : (
              <div className="p-6 bg-muted/30 border border-dashed rounded-lg text-center">
                <MapPin className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No location information available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );

  // Mobile version with Drawer
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} handleOnly={true}>
        <DrawerContent className="h-[100svh] max-h-[100svh] flex flex-col z-[100]" showCloseButton={false} showHandle={false}>
          {/* Sticky Back Button Header - flush at top */}
          <div className="sticky top-0 z-50 bg-background border-b border-border px-3 py-2.5 flex items-center gap-3 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-9 w-9 flex-shrink-0 hover:bg-muted"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <span className="font-medium text-sm text-foreground/80 truncate flex-1">Back to Marketplace</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={(e) => { e.stopPropagation(); toggleFavorite(listing.id, 'parts'); }}
                aria-label={isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart className={cn("w-4 h-4", isFavorite(listing.id) && "fill-red-500 text-red-500")} />
              </Button>
              <ShareListingButton
                listingId={listing.id}
                title={listing.title || listing.name}
                price={listing.price || listing.rental_rate || listing.max_budget || 0}
                location={[listing.city, listing.country].filter(Boolean).join(', ')}
                listingType={listing.listing_type}
                assetType="parts"
                variant="ghost"
                size="icon"
                className="h-9 w-9"
              />
            </div>
          </div>
          
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            <div className="px-4 pb-32 overflow-x-hidden">
              {/* Header section - Mobile optimized */}
              <div className="pt-4 pb-4 space-y-5">
                  
                  {/* Title Section */}
                  <div className="bg-muted/30 rounded-lg border border-border p-3">
                    <div className="flex items-center gap-2">
                      {/* Icon */}
                      <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center border border-border shadow-sm flex-shrink-0">
                        {getItemTypeIcon(listing.item_type)}
                      </div>
                      
                      {/* Title and badges */}
                      <div className="flex-1 min-w-0">
                        <DrawerTitle className="text-xl font-semibold text-foreground mb-2">
                          {listing.title || listing.name}
                        </DrawerTitle>
                        <DrawerDescription className="sr-only">
                          {getListingTypeLabel(listing.listing_type)} listing for {listing.title || listing.name}
                        </DrawerDescription>
                        
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className={cn("text-xs px-2 py-0.5 font-normal", getListingTypeBadgeStyle(listing.listing_type))}>
                            {getListingTypeLabel(listing.listing_type)}
                          </Badge>
                          
                          <Badge variant="outline" className="text-xs px-2 py-0.5 font-normal border-border text-muted-foreground gap-1">
                            {(listing.item_type || 'part').charAt(0).toUpperCase() + (listing.item_type || 'part').slice(1)}
                          </Badge>
                          
                          {listing.condition && (
                            <Badge variant="outline" className={cn("text-xs px-2 py-0.5 font-normal capitalize", getConditionColor(listing.condition))}>
                              {listing.condition.replace(/_/g, ' ')}
                            </Badge>
                          )}
                          
                          {listing.featured && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Specificity badges */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {listing.specificity_type === 'helicopter' && listing.helicopter_model && (
                        <Badge variant="outline" className="text-xs bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20">
                          For: {listing.helicopter_manufacturer} {listing.helicopter_model}
                        </Badge>
                      )}
                      {listing.specificity_type === 'engine' && listing.engine_model && (
                        <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20">
                          For: {listing.engine_manufacturer} {listing.engine_model}
                        </Badge>
                      )}
                      {listing.part_number && (
                        <Badge variant="secondary" className="text-xs font-mono">P/N: {listing.part_number}</Badge>
                      )}
                    </div>
                    
                    {listing.description && (
                      <p className="mt-2 text-muted-foreground text-sm line-clamp-3">
                        {listing.description}
                      </p>
                    )}
                  </div>

                  {/* Seller Section */}
                  {sellerProfile && (
                    <SellerSection profile={sellerProfile} className="mt-4" onNavigate={() => onOpenChange(false)} />
                  )}

                  {/* Metadata Card - properly contained */}
                  <div className="bg-background rounded-xl border border-border p-4 shadow-sm space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                      {shouldHideLocation ? (
                        <BlurredPrivateData
                          placeholderContent={<span className="font-medium">City, Country</span>}
                          fieldName="Location"
                          size="sm"
                          inline
                          onRequestInfo={isOwner ? undefined : () => requestField('location')}
                        />
                      ) : (
                        (listing.city || listing.country) ? (
                          <span className="font-medium break-words">{[listing.city, listing.country].filter(Boolean).join(', ')}</span>
                        ) : (
                          <span className="font-medium text-muted-foreground">Location not specified</span>
                        )
                      )}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                      {listing.quantity && listing.quantity > 1 && (
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                          <span>Qty: {listing.quantity}</span>
                        </div>
                      )}
                      {listing.warranty && (
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                          <span>{listing.warranty}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Price & Actions Section */}
                  <div className="bg-muted/50 rounded-lg border border-border p-3">
                    {/* Price/Budget section */}
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground mb-1">{isBuyRequest ? 'Budget' : listing.rental_rate ? 'Rate' : 'Price'}</p>
                      {shouldHidePrice ? (
                        <BlurredPrivateData
                          placeholderContent={<span className="text-2xl font-semibold text-foreground">$XX,XXX</span>}
                          fieldName="Price"
                          size="lg"
                          onRequestInfo={isOwner ? undefined : () => requestField('price')}
                        />
                      ) : (
                        <p className="text-2xl font-semibold text-foreground">
                          {listing.price ? `$${formatNumber(listing.price)}` :
                           listing.rental_rate ? `$${formatNumber(listing.rental_rate)}/${listing.rental_period === 'flight_hour' ? 'hr' : (listing.rental_period || 'day')}` :
                           listing.max_budget ? `Up to $${formatNumber(listing.max_budget)}` : 'Price on request'}
                        </p>
                      )}
                    </div>
                    
                    {/* Actions section - stacked for mobile */}
                    <div className="space-y-2">
                      <Button 
                        className={cn("w-full", isOwner ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" : "bg-foreground hover:bg-foreground/90 text-background")}
                        onClick={isOwner ? undefined : handleContact}
                        disabled={isOwner}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        {isOwner ? 'This is your listing' : isBuyRequest ? 'Contact Buyer' : 'Contact Seller'}
                      </Button>
                      
                      {isBuyRequest && !isOwner && (
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            const event = new CustomEvent('open-parts-buy-request-response', { detail: { request: listing } });
                            window.dispatchEvent(event);
                          }}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Offer Your Part
                        </Button>
                      )}
                      
                      <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={handleFavoriteToggle} className="flex-1 border-border" aria-label={isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}>
                          <Heart className={cn("w-4 h-4", (isFavorited || isFavorite(listing.id)) ? "fill-red-500 text-red-500" : "")} />
                        </Button>
                        
                        <ShareListingButton
                          listingId={listing.id}
                          title={listing.title || listing.name}
                          price={listing.price || listing.rental_rate || listing.max_budget || 0}
                          location={`${listing.city}, ${listing.country}`}
                          listingType={listing.listing_type}
                          assetType="parts"
                          variant="outline"
                          size="icon"
                          className="flex-1 border-border"
                        />
                      </div>
                    </div>
                  </div>

                </div>

              {/* Content area - Mobile optimized */}
              <div className="space-y-6">
                {renderPhotoGallery()}
                {renderTabs(true)}
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop version with Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[90vh] max-h-[90vh] p-0 overflow-hidden flex flex-col">
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-background to-muted/20 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-3">
              {/* Badges Row */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={cn("text-sm", getListingTypeBadgeStyle(listing.listing_type))}>
                  {getListingTypeLabel(listing.listing_type)}
                </Badge>
                <Badge variant="outline" className="gap-1">
                  {getItemTypeIcon(listing.item_type)}
                  {(listing.item_type || 'part').charAt(0).toUpperCase() + (listing.item_type || 'part').slice(1)}
                </Badge>
                {listing.condition && (
                  <Badge variant="outline" className={cn('capitalize', getConditionColor(listing.condition))}>
                    {listing.condition.replace(/_/g, ' ')}
                  </Badge>
                )}
                {listing.featured && (
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md">
                    <Star className="w-3 h-3 mr-1 fill-current" /> Featured
                  </Badge>
                )}
              </div>
              
              {/* Title */}
              <DialogTitle className="text-2xl md:text-3xl font-bold leading-tight">{listing.title || listing.name}</DialogTitle>
              <DialogDescription className="sr-only">
                {getListingTypeLabel(listing.listing_type)} listing for {listing.title || listing.name}
              </DialogDescription>
              
              {/* Specificity Badge */}
              <div className="flex items-center gap-2 flex-wrap">
                {listing.specificity_type === 'helicopter' && listing.helicopter_model && (
                  <Badge variant="outline" className="bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20">
                    For: {listing.helicopter_manufacturer} {listing.helicopter_model}
                  </Badge>
                )}
                {listing.specificity_type === 'engine' && listing.engine_model && (
                  <Badge variant="outline" className="bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20">
                    For: {listing.engine_manufacturer} {listing.engine_model}
                  </Badge>
                )}
                {listing.part_number && (
                  <Badge variant="secondary" className="font-mono">P/N: {listing.part_number}</Badge>
                )}
              </div>

              {/* Location */}
              {(listing.city || listing.country) && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {shouldHideLocation ? (
                    <BlurredPrivateData placeholderContent={<span>City, Country</span>} fieldName="Location" size="sm" inline onRequestInfo={isOwner ? undefined : () => requestField('location')} />
                  ) : (
                    <span>{[listing.city, listing.country].filter(Boolean).join(', ')}</span>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="outline" onClick={handleFavoriteToggle} className="gap-2 min-h-[44px]">
                <Heart className={cn("w-4 h-4", (isFavorited || isFavorite(listing.id)) ? "fill-red-500 text-red-500" : "")} />
                <span className="hidden sm:inline">Save</span>
              </Button>
              <ShareListingButton
                listingId={listing.id}
                title={listing.title || listing.name}
                price={listing.price || listing.rental_rate || listing.max_budget || 0}
                location={`${listing.city}, ${listing.country}`}
                listingType={listing.listing_type}
                assetType="parts"
                variant="outline"
                className="min-h-[44px]"
              />
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-6 py-6">
            {/* Price Card */}
            <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 mb-6">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                {renderPrice()}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button 
                    size="lg" 
                    className={cn("min-h-[48px]", isOwner ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" : "")}
                    onClick={isOwner ? undefined : handleContact}
                    disabled={isOwner}
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {isOwner ? 'This is your listing' : isBuyRequest ? 'Contact Buyer' : 'Contact Seller'}
                  </Button>
                  {isBuyRequest && !isOwner && (
                    <Button 
                      variant="outline"
                      size="lg"
                      className="min-h-[48px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        const event = new CustomEvent('open-parts-buy-request-response', { detail: { request: listing } });
                        window.dispatchEvent(event);
                      }}
                    >
                      <DollarSign className="w-5 h-5 mr-2" />
                      Offer Your Part
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {renderPhotoGallery()}
            {sellerProfile && (
              <SellerSection profile={sellerProfile} className="mb-4" onNavigate={() => onOpenChange(false)} />
            )}
            {renderTabs()}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default PartsListingDetailModal;
